// src/pages/services/FlightBooking.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const FlightBooking = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "I saved over ₹25,000 on my international flight to Europe thanks to Excellent Travels. Their customer service was outstanding throughout the process.",
            name: "Rajesh Khanna",
            location: "Delhi, India"
        },
        {
            content: "The flight booking process was seamless and they found me connections to Australia I couldn't find anywhere else. Highly recommended!",
            name: "Priya Malhotra",
            location: "Chennai, India"
        }
    ];

    const faqs = [
        {
            question: "When should I book my flight?",
            answer: "We recommend booking in advance to secure the best fares, especially for peak travel seasons. Our travel experts can advise you on the optimal booking window based on your specific travel plans."
        },
        {
            question: "Do you offer price matching if I find a cheaper fare elsewhere?",
            answer: "Yes! We offer a price match guarantee. Contact us if you find a lower price for the same flight, and we'll work to match it."
        },
        {
            question: "What happens if my flight gets cancelled?",
            answer: "If your flight is cancelled, our team will immediately work to find you alternative options. We'll contact you with available choices and help you rebook at no additional service fee."
        },
        {
            question: "Can I earn frequent flyer miles when booking through your agency?",
            answer: "Absolutely! You can add your frequent flyer number during the booking process to ensure you earn miles for your flights."
        }
    ];

    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);

    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="flight-booking" />
            <div className="service-hero">
                <img
                    src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1474&q=80"
                    alt="Flight Booking"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Flight Booking</h1>
                    <p className="service-subtitle">
                        Find the best deals on flights worldwide with our comprehensive booking system.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="https://cdn-icons-png.flaticon.com/512/3125/3125713.png"
                alt="Flight Booking Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/3125/3125713.png" alt="Flight Icon" className="section-icon" />
                        Fly to Your Dream Destinations
                    </h2>
                    <p>
                        At Excellent Travels, we make booking flights simple, affordable, and stress-free.
                        Our powerful search engine compares thousands of flights from major airlines and budget carriers
                        to find you the best deals available.
                    </p>
                    <p>
                        Whether you're planning a business trip, family vacation, or solo adventure,
                        our flight booking service offers:
                    </p>
                    <ul>
                        <li>Access to over 500 airlines worldwide</li>
                        <li>Exclusive deals and discounts not available elsewhere</li>
                        <li>Flexible booking options with free cancellation on select flights</li>
                        <li>24/7 customer support for any questions or changes</li>
                        <li>Price alerts to notify you when fares drop</li>
                        <li>Special domestic flight offers to all major Indian cities</li>
                        <li>International flight deals to popular destinations worldwide</li>
                    </ul>
                    <p>
                        Our travel experts can also help you find the perfect flight itinerary that
                        balances cost, convenience, and comfort according to your preferences.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2784/2784459.png" alt="Process Icon" className="section-icon" />
                        How It Works
                    </h2>
                    <p>
                        Booking your flight with us is quick and easy:
                    </p>
                    <ol>
                        <li>Enter your departure and arrival destinations</li>
                        <li>Select your travel dates and number of passengers</li>
                        <li>Browse through our curated list of flight options</li>
                        <li>Filter results based on your preferences (direct flights, specific airlines, etc.)</li>
                        <li>Select your preferred flight and complete your booking securely</li>
                    </ol>
                    <p>
                        After booking, you'll receive instant confirmation and e-tickets via email.
                        Our team remains available to assist with any changes or special requests.
                    </p>

                    {/* Feature Image */}
                    <div className="feature-image-container">
                        <img
                            src="https://images.unsplash.com/photo-1530521954074-e64f6810b32d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                            alt="Flight Booking Process"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Seamless booking experience with our user-friendly platform</div>
                    </div>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                        alt="Flight Booking"
                        className="sidebar-image"
                    />

                    <img
                        src="https://cdn-icons-png.flaticon.com/512/3125/3125713.png"
                        alt="Flight Booking Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Choose Our Flight Booking Service</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Price Icon" />
                            </span>
                            Best price guarantee
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png" alt="Fees Icon" />
                            </span>
                            No hidden fees or charges
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png" alt="Security Icon" />
                            </span>
                            Secure payment processing
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921222.png" alt="Confirmation Icon" />
                            </span>
                            Instant confirmation
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="Support Icon" />
                            </span>
                            24/7 customer support
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png" alt="Flexibility Icon" />
                            </span>
                            Flexible booking options
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933087.png" alt="Rewards Icon" />
                            </span>
                            Earn travel rewards
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Ready to Book Your Flight?</h2>
                <p>
                    Contact our travel experts today for personalized pricing and let us help you find the perfect flight for your next journey.
                </p>
                <Link to="/contact" className="cta-button">
                    Request a Quote
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Customers Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default FlightBooking;
